import { Link } from "react-router-dom";
import { ArrowRight, Check, ArrowUpRight } from "lucide-react";
import * as Icons from "lucide-react";
import { services } from "../mock";

const ServiceIcon = ({ name, className }) => {
  const Icon = Icons[name] || Icons.Sparkles;
  return <Icon className={className} strokeWidth={2.1} />;
};

const Services = () => {
  const featured = services.find((s) => s.featured);
  const rest = services.filter((s) => !s.featured);

  return (
    <section id="leistungen" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="reveal max-w-2xl">
          <span className="text-[13px] font-bold tracking-[0.22em] text-blue-600 uppercase">
            Unsere Leistungen
          </span>
          <h2 className="font-display mt-3 text-slate-900 text-[30px] sm:text-[44px] tracking-tight leading-[1.05]" style={{ fontWeight: 900 }}>
            Rundum-Reinigung aus einer Hand
          </h2>
          <p className="mt-4 text-[17px] text-slate-600">
            Als Ihre <strong className="font-semibold text-slate-800">Gebäudereinigung in Augsburg</strong> bieten
            wir alle Leistungen aus einer Hand – von der Büroreinigung bis zur
            Glasreinigung. Wählen Sie eine Leistung für alle Details.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-3 gap-6">
          {/* Featured card */}
          <Link
            to={`/leistungen/${featured.slug}`}
            className="reveal group text-left lg:row-span-2 relative rounded-3xl overflow-hidden shadow-lg ring-1 ring-slate-200 min-h-[420px] flex flex-col justify-end"
          >
            <img
              src={featured.image}
              alt={`${featured.title} in Augsburg – MAG Facility Services UG`}
              width="900"
              height="600"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-slate-900/10" />
            <div className="relative p-7 text-white">
              <span className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-[12px] font-bold tracking-wide">
                {featured.tag}
              </span>
              <h3 className="font-display mt-4 text-[26px]" style={{ fontWeight: 800 }}>
                {featured.title}
              </h3>
              <p className="mt-2 text-[15px] text-slate-200 max-w-md">
                {featured.short}
              </p>
              <ul className="mt-4 space-y-1.5">
                {featured.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-[14px] text-slate-100">
                    <Check className="w-4 h-4 text-blue-400" /> {b}
                  </li>
                ))}
              </ul>
              <span className="mt-5 inline-flex items-center gap-2 text-[14px] font-semibold text-white">
                Mehr erfahren
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>

          {/* Rest grid */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {rest.map((s) => (
              <Link
                key={s.id}
                to={`/leistungen/${s.slug}`}
                className="reveal group relative text-left bg-white rounded-2xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1"
              >
                <ArrowUpRight className="absolute top-5 right-5 w-5 h-5 text-slate-300 group-hover:text-blue-600 transition-colors" />
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <ServiceIcon name={s.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-display mt-4 text-[19px] text-slate-900" style={{ fontWeight: 700 }}>
                  {s.title}
                </h3>
                <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">
                  {s.short}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-blue-600">
                  Mehr erfahren
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Lokaler SEO-Textblock */}
        <div className="reveal mt-14 rounded-2xl bg-white border border-slate-200 p-7 lg:p-9">
          <h3 className="font-display text-[22px] text-slate-900" style={{ fontWeight: 700 }}>
            Ihre Gebäudereinigung in Augsburg
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
            Als regionaler Partner für die <strong className="font-semibold text-slate-800">Gebäudereinigung in Augsburg</strong> unterstützen
            wir Unternehmen, Praxen und Hausverwaltungen in der gesamten Region. Ob
            professionelle <strong className="font-semibold text-slate-800">Büroreinigung in Augsburg</strong>, hygienische
            <strong className="font-semibold text-slate-800"> Praxisreinigung in Augsburg</strong> oder streifenfreie
            <strong className="font-semibold text-slate-800"> Glasreinigung in Augsburg</strong> – wir sind schnell vor
            Ort und arbeiten zuverlässig, versichert und termintreu.
          </p>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
            Von der regelmäßigen Unterhaltsreinigung über Treppenhaus- und
            Grundreinigung bis zur Sonderreinigung: Profitieren Sie von kurzen
            Wegen, festen Ansprechpersonen und fairen Preisen für Ihre
            Gebäudereinigung in Augsburg und Umgebung – von Königsbrunn über
            Gersthofen bis Friedberg.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Services;
